const MAX_WAIT_FRAMES = 600;
const PIN_HOLD_DISTANCE = 180;
const REVEAL_DURATION_MS = 800;

/**
 * Performance statement motion, matching the Maston reference module:
 * CSS shiny-text wave plus the optional short pin hold. The section-level
 * entrance is owned by the shared section-entrance-reveal component.
 */
class MastonPerformanceStatement extends HTMLElement {
  #controller;
  #intersectionObserver;
  #isInViewport = false;
  #matchMedia;
  #revealTimer;
  #setupFrame;
  #waitAttempts = 0;

  connectedCallback() {
    if (this.#controller) return;

    this.#restoreStaticState();

    if (
      !this.hasAttribute('data-enable-sweep')
      || this.hasAttribute('data-statement-static')
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.setAttribute('data-statement-complete', '');
      return;
    }

    this.#controller = new AbortController();
    this.#waitAttempts = 0;
    window.addEventListener('pagehide', this.#onPageHide, { signal: this.#controller.signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal: this.#controller.signal });
    this.#observeSweep();
    if (this.hasAttribute('data-enable-pin-hold')) {
      this.#setupFrame = requestAnimationFrame(this.#waitForGsap);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;

    if (this.#setupFrame) cancelAnimationFrame(this.#setupFrame);
    this.#setupFrame = undefined;
    this.#clearRevealTimer();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
    this.#isInViewport = false;
    this.#destroyMotion();
  }

  #observeSweep() {
    if (this.#intersectionObserver) return;

    const heading = this.querySelector('[data-statement-heading]');
    if (!heading) {
      this.setAttribute('data-statement-complete', '');
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        const isInViewport = entry?.isIntersecting ?? false;
        if (isInViewport === this.#isInViewport) return;

        this.#isInViewport = isInViewport;

        if (!isInViewport) {
          this.#clearRevealTimer();
          this.#clearRevealState();
          this.removeAttribute('data-statement-wave');
          this.removeAttribute('data-statement-complete');
          return;
        }

        this.#startSweep();
      },
      { threshold: 0.25 }
    );
    this.#intersectionObserver.observe(heading);
  }

  #startSweep() {
    const text = this.querySelector('[data-statement-text]');
    if (!text) return;

    this.#clearRevealTimer();
    this.removeAttribute('data-statement-complete');
    this.removeAttribute('data-statement-wave');
    this.#clearRevealState();
    this.setAttribute('data-statement-reveal', '');
    this.#revealTimer = window.setTimeout(this.#startWave, REVEAL_DURATION_MS);
  }

  #startWave = () => {
    this.#revealTimer = undefined;
    if (!this.#isInViewport) return;

    this.#clearRevealState();
    this.setAttribute('data-statement-wave', '');
  };

  #clearRevealTimer() {
    if (this.#revealTimer === undefined) return;

    window.clearTimeout(this.#revealTimer);
    this.#revealTimer = undefined;
  }

  #clearRevealState() {
    this.removeAttribute('data-statement-reveal');
  }

  #waitForGsap = () => {
    if (!this.#controller || this.#controller.signal.aborted) return;
    if (this.#matchMedia) return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;

    if (gsap && ScrollTrigger) {
      this.#setupFrame = undefined;
      this.#setupMotion(gsap, ScrollTrigger);
      return;
    }

    this.#waitAttempts += 1;
    if (this.#waitAttempts >= MAX_WAIT_FRAMES) return;

    this.#setupFrame = requestAnimationFrame(this.#waitForGsap);
  };

  #onPageHide = () => {
    this.#destroyMotion();
  };

  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#restoreStaticState();
    this.#isInViewport = false;
    this.#observeSweep();
    if (this.hasAttribute('data-enable-pin-hold')) {
      this.#waitAttempts = 0;
      this.#waitForGsap();
    }
  };

  /**
   * @param {object} gsap
   * @param {object} ScrollTrigger
   */
  #setupMotion(gsap, ScrollTrigger) {
    const heading = this.querySelector('[data-statement-heading]');

    if (!heading) return;

    gsap.registerPlugin(ScrollTrigger);

    this.#matchMedia = gsap.matchMedia();
    this.#matchMedia.add(
      {
        isDesktop: '(min-width: 1280px)',
        isTablet: '(min-width: 768px) and (max-width: 1279px)',
        isMobile: '(max-width: 767px)',
        reduceMotion: '(prefers-reduced-motion: reduce)'
      },
      (context) => {
        const { reduceMotion } = context.conditions;

        if (reduceMotion) {
          this.setAttribute('data-statement-complete', '');
          return () => {
            heading.style.removeProperty('transform');
          };
        }

        this.setAttribute('data-enhanced', '');
        if (!this.#isInViewport) this.removeAttribute('data-statement-complete');

        const triggerId = `maston-performance-statement-${this.dataset.sectionId}`;
        const enablePinHold = this.hasAttribute('data-enable-pin-hold');

        const pinTrigger = enablePinHold
          ? ScrollTrigger.create({
              id: `${triggerId}-pin`,
              trigger: this,
              start: 'top 25%',
              end: `+=${PIN_HOLD_DISTANCE}`,
              pin: true,
              pinSpacing: false,
              invalidateOnRefresh: true
            })
          : null;

        return () => {
          pinTrigger?.kill();
          heading.style.removeProperty('transform');
          this.removeAttribute('data-enhanced');
        };
      }
    );

    this.#requestRefresh(ScrollTrigger);
    document.fonts?.ready.then(() => {
      if (this.isConnected && this.#matchMedia) this.#requestRefresh(ScrollTrigger);
    });
  }

  /**
   * @param {object} ScrollTrigger
   */
  #requestRefresh(ScrollTrigger) {
    if (typeof window.requestScrollTriggerRefresh === 'function') {
      window.requestScrollTriggerRefresh({ delay: 0 });
      return;
    }

    requestAnimationFrame(() => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    });
  }

  #destroyMotion() {
    this.#matchMedia?.revert();
    this.#matchMedia = undefined;
    this.#restoreStaticState();
  }

  #restoreStaticState() {
    this.#clearRevealTimer();
    this.removeAttribute('data-enhanced');
    this.#clearRevealState();
    this.removeAttribute('data-statement-wave');

    const heading = this.querySelector('[data-statement-heading]');
    heading?.style.removeProperty('transform');

    if (this.hasAttribute('data-statement-static') || !this.hasAttribute('data-enable-sweep')) {
      this.setAttribute('data-statement-complete', '');
      return;
    }

    this.removeAttribute('data-statement-complete');
  }
}

if (!customElements.get('maston-performance-statement')) {
  customElements.define('maston-performance-statement', MastonPerformanceStatement);
}
